import type { UseQueryResult } from "@tanstack/react-query"

import type { ResponseConfig } from "~/backend/axiosClient"
import {
  useFindCanonicalValueByCustomFieldValueId,
  useFindCanonicalValueByOperationId,
} from "~/gen/api"
import { datapointResponseVersionSnapshotDtoScopeEnum } from "~/gen/api/types/DatapointResponseVersionSnapshotDto.ts"
import type { ResolveDatapointResponseCanonicalValueResponseDto } from "~/gen/api/types/ResolveDatapointResponseCanonicalValueResponseDto.ts"

import type { DatapointScopeTarget } from "./datapoint-response-versions.types.ts"

type CanonicalValueQueryResult = UseQueryResult<
  ResponseConfig<ResolveDatapointResponseCanonicalValueResponseDto>,
  Error
>

export const useDatapointCanonicalValueQuery = (
  target: DatapointScopeTarget | null,
  enabled: boolean
): CanonicalValueQueryResult => {
  const isCustomFieldTarget =
    target?.scope ===
    datapointResponseVersionSnapshotDtoScopeEnum.CustomFieldValue
  const isOperationTarget =
    target?.scope === datapointResponseVersionSnapshotDtoScopeEnum.Operation

  const customFieldValueId = isCustomFieldTarget
    ? target.customFieldValueId
    : undefined

  const operationId = isOperationTarget ? target.operationId : undefined

  const customFieldQuery = useFindCanonicalValueByCustomFieldValueId(
    customFieldValueId,
    { query: { enabled: enabled && isCustomFieldTarget } }
  )

  const operationQuery = useFindCanonicalValueByOperationId(operationId, {
    query: { enabled: enabled && isOperationTarget },
  })

  if (isOperationTarget) {
    return operationQuery
  }

  return customFieldQuery
}
